import { useEffect, useRef, useState } from "react"

import { SelfieSegmentation } from "@mediapipe/selfie_segmentation"

import { cn } from "@vc/lib/utils"

interface VirtualBackgroundStreamProps {
  className?: string
  backgroundUrl: string
  onReady: (ready: boolean) => void
  /** The live camera stream to composite. Must be a resolved `MediaStream`
   *  before MediaPipe can start — pass `null` while you're still acquiring
   *  it, and the component will boot as soon as the prop becomes non-null. */
  stream: MediaStream | null
  videoFitMode?: "fit" | "fill"
  /** Lets the local recorder composite from this cutout canvas — the visible
   *  result here is a CSS background plus this canvas, so the recorder draws
   *  the background image under it. */
  tileId?: string
}

/**
 * Composites a live MediaStream against an image background using MediaPipe
 * selfie segmentation.
 *
 * Two implementation rules to keep this stable:
 *
 *  1. The effect that boots MediaPipe runs **once on mount** — `onReady` and
 *     `getSourceStream` are captured in refs so an inline-defined callback
 *     in the parent doesn't restart segmentation on every render. (That's
 *     what caused the black-frame "blinking" the user reported: the old
 *     effect listed both callbacks in its dep array → cleanup ran every
 *     parent render → mediapipe got torn down + rebuilt before the canvas
 *     could paint.)
 *
 *  2. The raw `<video>` stays visible until segmentation produces its first
 *     frame. Before that, painting the wrapper's `background-image` would
 *     show a static photo with no person in it, which reads as "my video
 *     stopped working." After the first segmented frame the canvas fades in
 *     and the raw video fades out.
 */
export function VirtualBackgroundStream({
  className,
  backgroundUrl,
  onReady,
  stream,
  videoFitMode,
  tileId,
}: VirtualBackgroundStreamProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const segmentationRef = useRef<SelfieSegmentation | null>(null)
  const frameReqRef = useRef<number | null>(null)

  // Stash callbacks in refs so the boot effect's deps stay stable. The
  // values themselves can change between renders without ever restarting
  // mediapipe.
  const onReadyRef = useRef(onReady)
  useEffect(() => {
    onReadyRef.current = onReady
  }, [onReady])

  // `composing` flips to true once mediapipe produces its first segmented
  // frame. See the file comment above for why this matters.
  const [composing, setComposing] = useState(false)

  useEffect(() => {
    // Wait for the camera stream to actually arrive before booting
    // MediaPipe. This effect re-runs every time the stream identity
    // changes, so the component recovers cleanly when the user grants
    // permission after the prejoin first mounts.
    if (!stream) {
      setComposing(false)
      return
    }

    let mounted = true

    const stopLoop = () => {
      if (frameReqRef.current) {
        window.cancelAnimationFrame(frameReqRef.current)
        frameReqRef.current = null
      }
    }

    const cleanup = () => {
      stopLoop()
      try {
        segmentationRef.current?.close?.()
      } catch {
        // ignore
      }
      segmentationRef.current = null
    }

    const start = async () => {
      try {
        if (!mounted) return
        if (!videoRef.current || !canvasRef.current) return

        videoRef.current.srcObject = stream
        // Ensure frames start flowing quickly (some browsers won't produce
        // frames if video is display:none).
        videoRef.current
          .play()
          .then(() => {
            // ignore
          })
          .catch(() => {
            // ignore
          })
        const v = videoRef.current
        const waitForData = () =>
          new Promise<void>((resolve) => {
            if (v.readyState >= 2 && v.videoWidth > 0) {
              resolve()
              return
            }
            const settle = () => {
              v.removeEventListener("loadeddata", settle)
              v.removeEventListener("canplay", settle)
              resolve()
            }
            v.addEventListener("loadeddata", settle)
            v.addEventListener("canplay", settle)
          })

        const segmentation = new SelfieSegmentation({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747/${file}`,
        })

        // `selfieMode` OFF, so the composite comes out in the same orientation
        // as the raw camera.
        //
        // It was on, which had MediaPipe mirror the pixels — and Flip Camera is
        // a CSS transform layered on top of that. Two mirrors for one setting:
        // with the flip off, applying a background mirrored the view that had
        // been unmirrored a moment earlier; with the flip on, the baked-in
        // mirror cancelled the transform and the view snapped back to
        // unflipped. Either way the orientation changed when a background was
        // applied, which is the one thing it must not do.
        //
        // `segmentation-source` — the instance behind the published stream —
        // has always used `false` for the same reason. The CSS transform is the
        // single source of truth for which way round the picture faces; nothing
        // underneath it may have an opinion.
        segmentation.setOptions({
          modelSelection: 1,
          selfieMode: false,
        })

        segmentation.onResults((results) => {
          if (!mounted) return
          const canvas = canvasRef.current
          if (!canvas) return
          const ctx = canvas.getContext("2d")
          if (!ctx) return

          const w = results.image.width
          const h = results.image.height

          if (canvas.width !== w) canvas.width = w
          if (canvas.height !== h) canvas.height = h

          ctx.save()
          ctx.clearRect(0, 0, w, h)

          // Meet-like compositing on a transparent canvas:
          // 1) draw the camera frame
          // 2) apply segmentationMask as alpha (keep only person)
          // Background is rendered by the wrapper div via CSS (avoids
          // CORS-tainted canvas).
          ctx.globalCompositeOperation = "source-over"
          ctx.drawImage(results.image, 0, 0, w, h)
          ctx.globalCompositeOperation = "destination-in"
          ctx.drawImage(results.segmentationMask, 0, 0, w, h)

          ctx.restore()

          // First frame landed — flip from "show raw video" to "show
          // composite". Subsequent calls are no-ops thanks to the bail-out
          // in setState.
          setComposing((prev) => (prev ? prev : true))
        })

        segmentationRef.current = segmentation

        const drawLoop = async () => {
          if (!mounted) return
          if (!videoRef.current) return

          // Ensure the element has decoded dimensions; some environments
          // keep `readyState` low for tiny/hidden videos even though frames
          // are available.
          if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
            frameReqRef.current = window.requestAnimationFrame(drawLoop)
            return
          }

          try {
            await segmentation.send({ image: videoRef.current })
          } catch {
            // ignore
          }

          frameReqRef.current = window.requestAnimationFrame(drawLoop)
        }

        await waitForData()
        if (!mounted) return

        onReadyRef.current(true)
        frameReqRef.current = window.requestAnimationFrame(drawLoop)
      } catch {
        if (!mounted) return
        onReadyRef.current(false)
      }
    }

    start()

    return () => {
      mounted = false
      cleanup()
    }
    // Re-run when (and only when) the underlying camera stream identity
    // changes — i.e. when it first arrives, or if the parent reassigns
    // tracks. `onReady` is captured via a ref above so the parent passing
    // an inline `() => {}` doesn't thrash the effect.
  }, [stream])

  return (
    <div
      className={cn(
        "relative h-full w-full overflow-hidden",
        videoFitMode === "fit" && "bg-black/80",
        className,
      )}
      style={{
        // Always paint the chosen background so picking a tile in the
        // dialog (or selecting one in the lobby) gives immediate visual
        // feedback. MediaPipe's cutout overlays on top via the canvas.
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: videoFitMode === "fit" ? "contain" : "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Hidden video element — MediaPipe samples from it; not shown. */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 h-full w-full opacity-0 pointer-events-none"
      />
      {/* Person cutout — drawn by MediaPipe over the background image. The
          fade-in masks the ~1s CDN model download on the very first frame. */}
      <canvas
        className={cn(
          "absolute inset-0 h-full w-full transition-opacity duration-200",
          videoFitMode === "fit" ? "object-contain" : "object-cover",
          composing ? "opacity-100" : "opacity-0",
        )}
        ref={canvasRef}
        data-tile-id={tileId}
      />
    </div>
  )
}
